import { type FC, useCallback, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { TriggerButton } from "widgets";

import { canDownloadLogs } from "@/app-utils/capability.ts";
import type { Capability, PlaybookEvent, User } from "@/graphql.ts";
import { downloadProcessorLogs } from "@/redux/modules/data-export.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";

interface DownloadProcessorLogsButtonProps {
  playbookEvent: PlaybookEvent | undefined;
}

export const DownloadProcessorLogsButton: FC<DownloadProcessorLogsButtonProps> = ({ playbookEvent }) => {
  const userSession: User = useSelector<IAppState, User>((state) => state.auth.session, shallowEqual);
  const dispatch = useDispatch<ReduxDispatch>();

  const userHasCapability = canDownloadLogs((userSession.capabilities ?? []) as Capability[]);

  // Never shown at the moment. But kept as a fallback
  const disabledTooltip = useMemo(() => {
    if (!userHasCapability) {
      return "You do not have permission to download log files.";
    } else if (!playbookEvent?.supportsLogFileDownload) {
      return "Download logs is only supported for Processor and Nextflow playbooks";
    } else if (playbookEvent?.eventExpired) {
      return "Playbook execution logs are not available.";
    } else if (playbookEvent?.executionLogsAvailable) {
      return "Playbook execution logs are not available yet.";
    }
    return undefined;
  }, [
    userHasCapability,
    playbookEvent?.supportsLogFileDownload,
    playbookEvent?.eventExpired,
    playbookEvent?.executionLogsAvailable,
  ]);

  const handlePress = useCallback(() => {
    if (playbookEvent?.id) {
      void dispatch(downloadProcessorLogs(playbookEvent.id));
    }
  }, [dispatch, playbookEvent?.id]);

  // We agreed to not show the button when capability is missing, the Playbook type is not supported or when the project is not visible for the user
  if (!(userHasCapability && playbookEvent?.supportsLogFileDownload && playbookEvent?.projectVisibleForUser)) {
    return null;
  }

  return (
    <TriggerButton
      id={"download-playbook-event-logs-button"}
      icon={"download"}
      onPress={handlePress}
      isDisabled={disabledTooltip}
      label={"Download Log Files"}
    />
  );
};
